import React, { useEffect, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { CheckCircle2, Trash2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ConfirmDialog from "@/components/ConfirmDialog";
import FreshnessBadge from "@/components/FreshnessBadge";
import StorageStatus from "@/components/StorageStatus";

export default function AdminUploads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/excel/uploads");
    setItems(data);
    // Purge selezioni orfane (upload eliminati esternamente)
    setSelectedIds((prev) => {
      const validIds = new Set(data.map((u) => u.id));
      const next = new Set();
      prev.forEach((id) => { if (validIds.has(id)) next.add(id); });
      return next;
    });
  };
  useEffect(() => { load(); }, []);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === items.length && items.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((u) => u.id)));
    }
  };
  const clearSelection = () => setSelectedIds(new Set());

  const del = (u) => {
    setConfirmState({
      title: "Eliminare questo caricamento?",
      message: (
        <>
          Il file <b>{u.original_filename}</b> del <b>{fmtDateTime(u.uploaded_at)}</b> verrà rimosso, insieme a tutti i prezzi e gli indicatori Dashboard associati a questo caricamento. L'azione è irreversibile.
        </>
      ),
      confirmLabel: "Elimina caricamento",
      variant: "danger",
      onConfirm: async () => {
        setBusy(u.id);
        try {
          const { data } = await api.delete(`/excel/uploads/${u.id}`);
          toast.success(`Caricamento eliminato · ${data.deleted_prices} prezzi e ${data.deleted_indicators} indicatori rimossi`);
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const delBulk = () => {
    const count = selectedIds.size;
    if (!count) return;
    setConfirmState({
      title: `Eliminare ${count} caricament${count === 1 ? "o" : "i"}?`,
      message: (
        <>
          Verranno rimossi <b>{count}</b> caricament{count === 1 ? "o" : "i"} Excel e in cascata TUTTI i prezzi
          e gli indicatori Dashboard associati. L'azione è irreversibile.
        </>
      ),
      confirmLabel: `Elimina ${count} caricament${count === 1 ? "o" : "i"}`,
      variant: "danger",
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/excel/uploads/bulk-delete", { ids: Array.from(selectedIds) });
          toast.success(`${data.deleted} eliminati · ${data.deleted_prices} prezzi e ${data.deleted_indicators} indicatori rimossi`);
          clearSelection();
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione in massa");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  return (
    <div className="space-y-5">
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Storico caricamenti Excel</h1>
        <p className="text-sm text-slate-500 mt-1">
          Elenco di tutti i file caricati.{" "}
          {isAdmin
            ? "Puoi eliminare un caricamento errato (o più contemporaneamente): verranno rimossi anche i prezzi e gli indicatori Dashboard associati."
            : "Solo un admin può eliminare i caricamenti."}
        </p>
        <div className="mt-3"><FreshnessBadge /></div>
      </div>

      {isAdmin && selectedIds.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="bulk-delete-bar">
          <div className="text-sm text-red-800 font-medium">
            {selectedIds.size} caricament{selectedIds.size === 1 ? "o selezionato" : "i selezionati"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              disabled={bulkBusy}
              className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:border-slate-400 font-semibold disabled:opacity-40"
              data-testid="bulk-clear"
            >
              Deseleziona
            </button>
            <button
              onClick={delBulk}
              disabled={bulkBusy}
              className="text-xs px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center gap-1.5 disabled:opacity-40"
              data-testid="bulk-delete-btn"
            >
              {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Elimina selezionati
            </button>
          </div>
        </div>
      )}

      <div className="card-elevated overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              {isAdmin && (
                <th className="w-10 text-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    disabled={items.length === 0}
                    className="rounded"
                    aria-label="Seleziona tutti"
                    data-testid="uploads-select-all"
                  />
                </th>
              )}
              <th>File</th>
              <th>Fogli</th>
              <th>Righe</th>
              <th>Data</th>
              <th>Stato</th>
              <th className="w-28 text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className={selectedIds.has(u.id) ? "bg-red-50/50" : ""}>
                {isAdmin && (
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleOne(u.id)}
                      className="rounded"
                      aria-label={`Seleziona ${u.original_filename}`}
                      data-testid={`upload-select-${u.id}`}
                    />
                  </td>
                )}
                <td className="!font-sans">{u.original_filename}</td>
                <td>{(u.sheets_found || []).join(", ")}</td>
                <td>{u.imported_rows_count}</td>
                <td className="text-xs">{fmtDateTime(u.uploaded_at)}</td>
                <td>
                  <span className="badge-pos"><CheckCircle2 size={11} /> {u.status}</span>
                  <StorageStatus u={u} />
                </td>
                <td className="text-center whitespace-nowrap">
                  {u.file_available ? (
                  <a
                    href={`${API}/excel/uploads/${u.id}/download?auth=${encodeURIComponent(getToken() || "")}`}
                    className="inline-block p-2 text-slate-500 hover:text-blue-600"
                    title="Scarica file .xlsx originale"
                    data-testid={`upload-download-${u.id}`}
                    download={u.original_filename}
                  >
                    <Download size={15} />
                  </a>
                  ) : (
                    <span className="inline-block p-2 text-slate-200" title="File non più disponibile" data-testid={`upload-download-disabled-${u.id}`}>
                      <Download size={15} />
                    </span>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => del(u)}
                      disabled={busy === u.id}
                      className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-40"
                      aria-label="Elimina caricamento"
                      title="Elimina questo caricamento"
                      data-testid={`upload-delete-${u.id}`}
                    >
                      {busy === u.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-400">Nessun upload</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
